// src/components/SummaryDrill.jsx
// 出席記録（授業のまとめ）の数字から「誰が当てはまるか」を見る・名簿にいない学生を片付ける【2026-10-07】
//
//   ・useMatrix … 学生×回の出欠（GET /api/schedule/classes/:id/attendance-matrix）を読む
//   ・StudentList … 当てはまる学生の一覧（数字を押したときに開く）
//   ・OutsiderPanel … 名簿にいない学生の打刻を、履修者に加える／なかったことにする／別の授業へ移す
//                     （POST /api/class-summary/:classId/outsiders）
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { confirmDialog } from './ConfirmDialog';

const ATTENDED = ['present', 'late', 'early_leave'];
const LABEL = { present: '出席', late: '遅刻', early_leave: '早退', excused: '公欠', irregular: '確認待ち', absent: '欠席' };
const md = (d) => { const s = String(d || '').slice(0, 10).split('-'); return s.length === 3 ? `${Number(s[1])}/${Number(s[2])}` : ''; };

export function useMatrix(classId, auth) {
  const [matrix, setMatrix] = useState(null);
  const load = async () => {
    try {
      const r = await fetch('/api/schedule/classes/' + classId + '/attendance-matrix', { headers: auth() });
      const d = await r.json();
      setMatrix(r.ok && d.success ? d : { sessions: [], students: [] });
    } catch (e) { setMatrix({ sessions: [], students: [] }); }
  };
  useEffect(() => { setMatrix(null); load(); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { matrix, reloadMatrix: load };
}

/** 名簿の学生・名簿にいない学生に分ける（名簿がまだ無い授業は全員を名簿の学生として扱う。出席率の計算と同じ） */
export function splitStudents(matrix) {
  const all = (matrix && matrix.students) || [];
  const hasRoster = all.some(s => !s.is_outside_roster);
  return {
    hasRoster,
    enrolled: hasRoster ? all.filter(s => !s.is_outside_roster) : all,
    outsiders: hasRoster ? all.filter(s => s.is_outside_roster) : [],
  };
}

/** 授業全体の数字 → 当てはまる学生 */
export function rowsForOverview(matrix, kind) {
  const { enrolled, outsiders } = splitStudents(matrix);
  const count = (s, st) => Object.values(s.cells || {}).filter(x => x === st).length;
  if (kind === 'enrolled') return enrolled.map(s => ({ ...s, note: `出席 ${s.attended}/${s.held}回${s.rate != null ? `（${s.rate}%）` : ''}` }));
  if (kind === 'outsiders') return outsiders.map(s => ({ ...s, note: `打刻 ${Object.keys(s.cells || {}).length}回` }));
  if (kind === 'late' || kind === 'early_leave' || kind === 'excused') {
    return enrolled.filter(s => count(s, kind) > 0)
      .map(s => ({ ...s, n: count(s, kind), note: `${LABEL[kind]} ${count(s, kind)}回` }))
      .sort((a, b) => b.n - a.n);
  }
  return [];
}

/** 回の数字 → 当てはまる学生 */
export function rowsForSession(matrix, sessionId, kind) {
  const { enrolled, outsiders } = splitStudents(matrix);
  const st = (s) => (s.cells || {})[sessionId];
  if (kind === 'attended') return enrolled.filter(s => ATTENDED.includes(st(s))).map(s => ({ ...s, note: LABEL[st(s)] }));
  if (kind === 'late' || kind === 'early_leave') return enrolled.filter(s => st(s) === kind).map(s => ({ ...s, note: LABEL[kind] }));
  if (kind === 'absent') return enrolled.filter(s => !ATTENDED.includes(st(s))).map(s => ({ ...s, note: st(s) ? LABEL[st(s)] || st(s) : '記録なし' }));
  if (kind === 'outsiders') return outsiders.filter(s => st(s)).map(s => ({ ...s, note: LABEL[st(s)] || st(s) }));
  return [];
}

/** 当てはまる学生の一覧 */
export function StudentList({ title, rows, onClose, classId, loading }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0.5rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <strong style={{ flex: 1, fontSize: '0.92rem' }}>{title}{!loading && `（${rows.length}人）`}</strong>
        {classId && <Link to={'/classes/' + classId + '/edit'} style={{ fontSize: '0.8rem' }}>出欠の確認・修正で直す →</Link>}
        <button type="button" className="btn-secondary" style={{ padding: '0.1rem 0.6rem', fontSize: '0.8rem' }} onClick={onClose}>閉じる</button>
      </div>
      {loading ? <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>読み込み中…</div>
        : rows.length === 0 ? <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>当てはまる学生はいません</div>
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 0.5rem', maxHeight: 220, overflowY: 'auto' }}>
            {rows.map(s => (
              <span key={s.id} style={{ fontSize: '0.84rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.15rem 0.5rem' }}
                    title={s.student_id}>
                {s.name || '（氏名なし）'}<span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: 4 }}>{s.student_id}</span>
                {s.note && <span style={{ color: '#4b5563', fontSize: '0.75rem', marginLeft: 6 }}>{s.note}</span>}
              </span>
            ))}
          </div>
        )}
    </div>
  );
}

/** 名簿にいない学生の打刻を片付ける */
export function OutsiderPanel({ classId, matrix, auth, setErr, setMsg, onDone }) {
  const { hasRoster, outsiders } = useMemo(() => splitStudents(matrix), [matrix]);
  const sessions = (matrix && matrix.sessions) || [];
  const [checked, setChecked] = useState({});
  const [classes, setClasses] = useState(null);
  const [moveTo, setMoveTo]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [open, setOpen]       = useState(false);
  const ids = outsiders.filter(s => checked[s.id]).map(s => s.id);

  useEffect(() => { setChecked({}); }, [matrix]);
  const loadClasses = async () => {
    if (classes !== null) return;
    try {
      const r = await fetch('/api/schedule/classes', { headers: auth() });
      const d = await r.json();
      const list = (d.classes || d.data || []).filter(c => String(c.id) !== String(classId));
      setClasses(list);
    } catch (e) { setClasses([]); }
  };

  if (!matrix || !hasRoster || outsiders.length === 0) return null;

  const run = async (action, userIds) => {
    if (!userIds.length) { setErr('学生を選んでください'); return; }
    const names = outsiders.filter(s => userIds.includes(s.id)).map(s => s.name || s.student_id);
    const nRec = outsiders.filter(s => userIds.includes(s.id)).reduce((a, s) => a + Object.keys(s.cells || {}).length, 0);
    const dest = action === 'move' ? (classes || []).find(c => String(c.id) === String(moveTo)) : null;
    if (action === 'move' && !dest) { setErr('移す先の授業を選んでください'); return; }
    const q = action === 'enroll'
      ? `${names.length}人（${names.slice(0, 5).join('、')}${names.length > 5 ? ' ほか' : ''}）を履修者名簿に加えます。確認待ちの打刻は出席になります。`
      : action === 'discard'
        ? `${names.length}人（${names.slice(0, 5).join('、')}${names.length > 5 ? ' ほか' : ''}）の、この授業の打刻 ${nRec}件をなかったことにします（消えます）。`
        : `${names.length}人の打刻 ${nRec}件を「${dest.name}」の同じ日の回へ移します。`;
    if (!(await confirmDialog(q + '\nよろしいですか？'))) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/class-summary/' + classId + '/outsiders', {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, userIds, ...(action === 'move' ? { toClassId: Number(moveTo) } : {}) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
      setMsg(d.message);
      if (onDone) onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const allOn = ids.length === outsiders.length;
  const recs = outsiders.reduce((a, s) => a + Object.keys(s.cells || {}).length, 0);
  return (
    <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <strong style={{ flex: 1 }}>
          名簿にいない学生の打刻：{outsiders.length}人・{recs}件
          <span style={{ fontWeight: 400, fontSize: '0.82rem', color: '#6b7280', marginLeft: '0.5rem' }}>
            出席率には入っていません。履修者に加える・なかったことにする・別の授業へ移す、を選べます
          </span>
        </strong>
        <button type="button" className="btn-secondary" onClick={() => { setOpen(!open); if (!open) loadClasses(); }}>
          {open ? '閉じる' : '学生を見る・片付ける'}
        </button>
        {!open && (
          <button type="button" disabled={busy} onClick={() => run('discard', outsiders.map(s => s.id))}
                  style={{ color: '#b91c1c' }}>全員なかったことにする</button>
        )}
      </div>
      {open && (
        <>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', margin: '0.6rem 0' }}>
            <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>選んだ {ids.length}人を：</span>
            <button className="btn-primary" disabled={busy || !ids.length} onClick={() => run('enroll', ids)}>履修者に加える</button>
            <button disabled={busy || !ids.length} onClick={() => run('discard', ids)} style={{ color: '#b91c1c' }}>なかったことにする</button>
            <select value={moveTo} onChange={e => setMoveTo(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">{classes === null ? '授業を読み込み中…' : '移す先の授業を選ぶ'}</option>
              {(classes || []).map(c => <option key={c.id} value={c.id}>{c.name}{c.semester ? `（${c.semester}）` : ''}</option>)}
            </select>
            <button className="btn-secondary" disabled={busy || !ids.length || !moveTo} onClick={() => run('move', ids)}>へ移す</button>
          </div>
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" checked={allOn} onChange={e => setChecked(e.target.checked ? Object.fromEntries(outsiders.map(s => [s.id, true])) : {})} />
                  </th>
                  <th>学籍番号</th><th>氏名</th><th>打刻した回</th><th></th>
                </tr>
              </thead>
              <tbody>
                {outsiders.map(s => {
                  const days = sessions.filter(x => (s.cells || {})[x.id]);
                  return (
                    <tr key={s.id}>
                      <td><input type="checkbox" checked={!!checked[s.id]} onChange={e => setChecked(c => ({ ...c, [s.id]: e.target.checked }))} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{s.student_id}</td>
                      <td>{s.name || '—'}</td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {days.map(x => `第${x.no}回 ${md(x.date)}（${LABEL[s.cells[x.id]] || s.cells[x.id]}）`).join('・')}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn-secondary" style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem', marginRight: 4 }}
                                disabled={busy} onClick={() => run('enroll', [s.id])}>追加</button>
                        <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem', color: '#b91c1c' }}
                                disabled={busy} onClick={() => run('discard', [s.id])}>削除</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 0 }}>
            「別の授業へ移す」は、隣の教室の授業に打刻してしまった学生などを、本来の授業の同じ日の回へ移します（その日に回が無ければ作ります）。
            移す先にもう記録がある回は、移す先の記録を残します。消した・移した記録は、変更の履歴に残ります。
          </p>
        </>
      )}
    </div>
  );
}
